import {
  ChatbotSentimentLabels,
  ChatbotIntentLabels,
  CustomerJourneyIntentTypeLabels,
  IdentificationStatusLabels,
  MomentLabels,
  ReportRow,
  ResolveLinkTypeLabels,
} from './../models/reports';
import { JourneyIntentLabels } from '../../_models/funnel';
import {
  ReportSessionInput,
  Session,
  SessionResult,
  SessionResultLabels,
  SessionSourceLabels,
  SessionTypeLabels,
  TimeData,
  CardType,
  TranTypeLabel,
  TransactionPaymentSourceLabel,
  TranMatchedStatusLabel,
  AlertSourceLabel,
  RequestTypeLabel,
  getCRMCustomerHtml,
  getCRMOrderHtml,
  getCardTypeValue,
  StepCategoryLabels,
  abbrOfferStageLabel,
  OfferTypes,
  DayOfWeekLabels,
  ResolveLookupOutcomeLabels,
} from "../../_models";
import {fixMouseflowRecordingUrl, formatTime, formatBillingIntervalDays} from "../../_helpers";
import * as moment from "moment-timezone";

const convertToUSD = (value) => {
  if (!value) {
    return "$0.00";
  }

  value = parseFloat(value.toFixed(2)).toLocaleString();
  value = value.split(".");
  if (value.length == 1) {
    return `$${value.join(".")}.00`;
  }

  if (value[1].length > 1) {
    return "$" + value.join(".");
  }

  return "$" + value.join(".") + "0";
};

const formatPct = (value: any): string => (value !== null && value !== undefined && value !== '' ? `${parseFloat(parseFloat(value).toFixed(2))}%` : '0%');

const formatDurationSeconds = (value: any): string => {
  let result = '--';
  const seconds = Number(value);
  if (value !== null && value !== undefined && !isNaN(seconds) && seconds > 0) {
    result = `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`;
  }
  return result;
};

const countOrZero = (value: any): number => (value !== null && value !== undefined ? Number(value) : 0);

const countWithPct = (field: string) => (row: any, value: any): string | number => {
  let result: string | number = countOrZero(value);
  const pct = row[field + '_pct'];
  if (pct !== null && pct !== undefined) {
    result = String(result) + '&ensp;(' + formatPct(pct) + ')';
  }
  return result;
};

const pairLabel = (nameKey: string, idKey: string) => (row: ReportRow): string => {
  let result = '--';
  if (row[nameKey]) {
    result = String(row[nameKey]);
  }
  if (row[idKey]) {
    result = row[nameKey] ? result + ' (' + row[idKey] + ')' : String(row[idKey]);
  }
  return result;
};

const enumLabel = (labels: { [value: number]: string }, value: any): string => {
  let result = '--';
  if (value !== null && value !== undefined && labels[value] !== undefined) {
    result = labels[value];
  }
  return result;
};

export const isRateOrPctField = (fieldName: string): boolean => fieldName.endsWith('_rate') || fieldName.endsWith('_pct');

const formatDateTimeEST = (dateTimeValue) => {
  if (!dateTimeValue) {
    return '';
  }
  
  const dateTimeMoment = moment(dateTimeValue).tz('EST');
  const time = dateTimeMoment.format('hh:mm:ss A');
  const date = dateTimeMoment.format('MMM DD, YYYY');
  return `${date} - ${time}`;
};

export const reportsFieldDataV2 = {
    username: {
      label: 'Session ID', getter: (sessionInput: ReportSessionInput, value: any) =>
        value ? value.substr(8) : ''
    },
    result: {
      label: 'Session result',
      getter: (sessionInput: ReportSessionInput, value: any) => value
    },
    lifeline: {
      label: 'Lifeline',
      getter: (sessionInput: ReportSessionInput, value: any) => value
    },

    date: {
      label: 'Date',
      getter: (sessionInput: ReportSessionInput, value: any) => value},

    week: {
      label: 'Week',
      getter: (sessionInput: ReportSessionInput, value: any) => value
    },

    month: {
      label: 'Month',
      getter: (sessionInput: ReportSessionInput, value: any) => value
    },

    year: {
      label: 'Year',
      getter: (sessionInput: ReportSessionInput, value: any) => value
    },

    hour: {
      label: 'Hours',
      getter: (sessionInput: ReportSessionInput, value: any) => value !== null ? value : 0
    },

    bypass: {
      label: 'Bypass',
      getter: (sessionInput: ReportSessionInput, value: any) => value
    },
    bypass_type: {
      label: 'Bypass Type',
      getter: (sessionInput: ReportSessionInput, value: any) => value
    },
    source: {
      label: 'Path channel',
      getter: (sessionInput: ReportSessionInput, value: any) => value
    },
    lifeline_type: {
      label: 'Lifeline Type',
      getter: (sessionInput: ReportSessionInput, value: any) => value
    },
    status: {label: 'Status', getter: (sessionInput: ReportSessionInput, value: any) => value},
    aff_id: {label: 'Affiliate', getter: (sessionInput: ReportSessionInput, value: any) => value},
    affiliate: {label: 'Affiliate', getter: (sessionInput: ReportSessionInput, value: any) => value},
    sid1: {label: 'Source sub 1', getter: (sessionInput: ReportSessionInput, value: any) => value},
    sid2: {label: 'Source sub 2', getter: (sessionInput: ReportSessionInput, value: any) => value},
    sid3: {label: 'Source sub 3', getter: (sessionInput: ReportSessionInput, value: any) => value},
    tracking_source: {
      label: 'Path Channel Source',
      custom_label:'Traffic Source',
      getter: (sessionInput: ReportSessionInput, value: any) => value
    },
    campaign_name: {
      label: 'Brand', getter: (sessionInput: ReportSessionInput, value: any) => value
    },
    customer_id: {
      label: 'Customer ID',
      getter: (sessionInput: ReportSessionInput, value: any) => getCRMCustomerHtml(value, sessionInput.crm_type)
    },
    country: {
      label: 'Country',
      getter: (sessionInput: ReportSessionInput, value: any) => value
    },
    browser_name: {
      label: 'Browser',
      getter: (sessionInput: ReportSessionInput, value: any) => value
    },
    os_version: {
      label: 'OS Version',
      getter: (sessionInput: ReportSessionInput, value: any) => value
    },
    device_type: {
      label: 'Device Type',
      getter: (sessionInput: ReportSessionInput, value: any) => value
    },
    mobile_vendor: {
      label: 'Device Brand',
      getter: (sessionInput: ReportSessionInput, value: any) => value
    },
    mobile_model: {
      label: 'Device Model',
      getter: (sessionInput: ReportSessionInput, value: any) => value
    },
    carrier_type: {
      label: 'Phone Type',
      getter: (sessionInput: ReportSessionInput, value: any) => value
    },
    'category': {
      label: 'Category', getter: (sessionInput: ReportSessionInput, value: any) => value
    },
    product_name: {
      label: 'Product Name', getter: (sessionInput: ReportSessionInput, value: any) => value
    },
    order_id: {
      label: 'Order ID', getter: (sessionInput: ReportSessionInput, value: any) => getCRMOrderHtml(value, value, sessionInput.crm_type)
    },
    sub1: {
      label: 'Aff sub 1', getter: (sessionInput: ReportSessionInput, value: any) => value
    },
    sub2: {
      label: 'Aff sub 2', getter: (sessionInput: ReportSessionInput, value: any) => value
    },
    sub3: {
      label: 'Aff sub 3', getter: (sessionInput: ReportSessionInput, value: any) => value
    },
    revenue: {
      label: 'Revenue', getter: (sessionInput: ReportSessionInput, value: any) => convertToUSD(value)
    },
    revenue_type: {
      label: 'Revenue type', getter: (sessionInput: ReportSessionInput, value: any) => value
    },
    downsell: {
      label: 'Downsells', getter: (sessionInput: ReportSessionInput, value: any) => value !== null ? value : 0
    },
    upsell: {
      label: 'Upsells', getter: (sessionInput: ReportSessionInput, value: any) => value !== null ? value : 0
    },
    save_amount: {
      label: 'Save amount', summary_label:'Revenue Saved', custom_label:'Saved', getter: (sessionInput: ReportSessionInput, value: any) => convertToUSD(value)
    },
    item_refund_quantity: {
      label: 'Refund quantity', custom_label: 'Refund Qty', getter: (sessionInput: ReportSessionInput, value: any) => value !== null ? value : 0
    },
    total_sessions: {
      label: 'Total Sessions', custom_label: 'Total Sessions', getter: (sessionInput: ReportSessionInput, value: any) => value !== null ? value : 0
    },
    avg_message_count: {
      label: 'Average Message Count', custom_label: 'Avg Message Count', getter: (sessionInput: ReportSessionInput, value: any) => value !== null ? value : 0
    },
    refund_amount: {
      label: 'Refund amount', custom_label: 'Refund', getter: (sessionInput: ReportSessionInput, value: any) => convertToUSD(value)
    },
    return_amount: {
      label: 'Return amount', getter: (sessionInput: ReportSessionInput, value: any) => convertToUSD(value)
    },
    return_quantity: {
      label: 'Return quantity', custom_label: 'Returns Executed', getter: (sessionInput: ReportSessionInput, value: any) => value !== null ? value : 0
    },
    downsell_take_rate: {
      label: 'Downsell take rate', getter: (sessionInput: ReportSessionInput, value: any) => value? `${(value * 100).toFixed(2)}%`: '0.00%'
    },
    revisions: {
      label: 'Exchangeables', getter: (sessionInput: ReportSessionInput, value: any) => value !== null ? value : 0
    },
    exchangeable_take_rate: {
      label: 'Exchangeable take rate', getter: (sessionInput: ReportSessionInput, value: any) => value? `${(value * 100).toFixed(2)}%`: '0.00%'
    },
    upsell_take_rate: {
      label: 'Upsell take rate', getter: (sessionInput: ReportSessionInput, value: any) => value? `${(value * 100).toFixed(2)}%`: '0.00%'
    },
    save_rate: {
      label: 'Save rate', custom_label:'Retention Rate', getter: (sessionInput: ReportSessionInput, value: any) => value? `${(value * 100).toFixed(2)}%`: '0.00%'
    },
    total_save_rate: {
      label: 'Save rate', getter: (sessionInput: ReportSessionInput, value: any) => value? `${(value * 100).toFixed(2)}%`: '0.00%'
    },
    refund_rate: {
      label: 'Refund rate', getter: (sessionInput: ReportSessionInput, value: any) => value? `${(value * 100).toFixed(2)}%`: '0.00%'
    },
    rma_rate: {
      label: 'RMA rate', getter: (sessionInput: ReportSessionInput, value: any) => value? `${(value * 100).toFixed(2)}%`: '0.00%'
    },
    cancel_rate: {
      label: 'Cancel rate', getter: (sessionInput: ReportSessionInput, value: any) => value? `${(value * 100).toFixed(2)}%`: '0.00%'
    },
    crm_campaign_name: {
      label: 'CRM campaign', getter: (sessionInput: ReportSessionInput, value: any) => value
    },
    cancel_reason: {
      label: 'Cancel reason', getter: (sessionInput: ReportSessionInput, value: any) => value
    },
    funnel_item_status: {
      label: 'Order status', getter: (sessionInput: ReportSessionInput, value: any) => value
    },
    order_status: {
      label: 'Order status', getter: (sessionInput: ReportSessionInput, value: any) => value
    },
    partial_refund_rate: {
      label: 'Partial refund rate', getter: (sessionInput: ReportSessionInput, value: any) => value? `${(value * 100).toFixed(2)}%`: '0.00%'
    },
    full_refund_rate: {
      label: 'Success Rate', getter: (sessionInput: ReportSessionInput, value: any) => {
        if (value !== null && value < 1) {
          return `${((1 - value) * 100).toFixed(2)}%`
        }

        return '0.00%'
      }
    },
    recording_url: {
      label: 'Video Recording', getter: (sessionInput: ReportSessionInput, value: any) => {
        if (!value) {
          return '';
        }

        return fixMouseflowRecordingUrl(value);
      }
    },
    phone_recording_url: {
      label: 'Audio Recording', getter: (sessionInput: ReportSessionInput, value: any) => value
    },
    exchangeable_product_name: {
      label: 'Exchangeable product', getter: (sessionInput: ReportSessionInput, value: any) => value
    },
    crm_type: {
      label: 'Platform', getter: (sessionInput: ReportSessionInput, value: any) => value
    },
    session_count: {
      label: 'Session quantity', custom_label: 'Sessions', getter: (sessionInput: ReportSessionInput, value: any) => value !== null ? value : 0
    },
    item_partial_refund_amount: {
      label: 'Partial refund amount', custom_label:'Partial Refund', getter: (sessionInput: ReportSessionInput, value: any) => convertToUSD(value)
    },
    full_refund_amount: {
      label: 'Full refund amount', custom_label:'Full Refund', getter: (sessionInput: ReportSessionInput, value: any) => convertToUSD(value)
    },
    refund_status_conditional: {
      label: 'Refund status', getter: (sessionInput: ReportSessionInput, value: any) => value
    },
    downsell_step_name: {
      label: 'Downsell Variation', getter: (sessionInput: ReportSessionInput, value: any) => value
    },
    campaign_product_id: {
      label: 'Campaign product id', getter: (sessionInput: ReportSessionInput, value: any) => value
    },
    campaign_product_ids: {
      label: 'Campaign product id', getter: (sessionInput: ReportSessionInput, value: any) => value
    },
    amount_paid: {
      label: 'Total billed', custom_label:'Total Billed', getter: (sessionInput: ReportSessionInput, value: any) => convertToUSD(value)
    },
    total_count_sum: {
      label: 'Actions total', getter: (sessionInput: ReportSessionInput, value: any) => value !== null ? value : 0
    },
    is_billable: {
      label: 'Billable', getter: (sessionInput: ReportSessionInput, value: any) => {
        if (value === null || value === '') {
          return ''
        } else if (value) {
          return 'Billable'
        }
        return 'Non-Billable'
      }
    },
    frequency: {
      label: 'Frequency', getter: (sessionInput: ReportSessionInput, value: any) => value !== null ? value : 0
    },
    steps_count: {
      label: 'Steps Count', getter: (sessionInput: ReportSessionInput, value: any) => value !== null ? value : 0
    },
    entered_input: {
      label: 'Key Prompts', getter: (sessionInput: ReportSessionInput, value: any) => value? value: ''
    },
    action_result: {
      label: 'Session Action Result', getter: (sessionInput: ReportSessionInput, value: any) => value? value: ''
    },
    session_action_result: {
      label: 'Session Action Result', getter: (sessionInput: ReportSessionInput, value: any) => value? value: ''
    },
    latest_result: {
      label: 'Latest Result', getter: (sessionInput: ReportSessionInput, value: any) =>
        value ? value : ''
    },
    latest_result_based_on_execution: {
      label: 'Latest result based on time', getter: (sessionInput: ReportSessionInput, value: any) =>
        value ? value : ''
    },
    product_path: {
      label: 'Stage', getter: (sessionInput: ReportSessionInput, value: any) => value ? value : ''
    },
    subscription_path: {
      label: 'Stage', getter: (sessionInput: ReportSessionInput, value: any) => value ? value : ''
    },
    rma_path: {
      label: 'Stage', getter: (sessionInput: ReportSessionInput, value: any) => value ? value : ''
    },
    hold_path: {
      label: 'Stage', getter: (sessionInput: ReportSessionInput, value: any) => value ? value : ''
    },
    downsell_attempt: {
      label: 'Downsell Attempts', getter: (sessionInput: ReportSessionInput, value: any) => value !== null ? value : 0
    },
    exchangeable_product_id: {
      label: 'Exchangeable Product ID', getter: (sessionInput: ReportSessionInput, value: any) => value ? value : ''
    },
    upsell_product_id: {
      label: 'Upsell Product', getter: (sessionInput: ReportSessionInput, value: any) => value ? value : ''
    },
    exchangeable_product_price: {
      label: 'Exchangeable Product Price', getter: (sessionInput: ReportSessionInput, value: any) => convertToUSD(value)
    },
    account_name: {
      label: 'Account', getter: (sessionInput: ReportSessionInput, value: any) => value ? value : ''
    },
    search_phrase: {
      label: 'Search Phrase', getter: (row, value: any) => value ? value : ''
    },
    session_status: {
      label: 'Session Status', getter: (sessionInput: ReportSessionInput, value: any) => value
    },
    number_of_searches: {
      label: 'Number of Searches', getter: (row, value: any) => value !== null ? value : 0
    },
    results_shown: {
      label: 'Number of results shown', getter: (row, value: any) => value !== null ? value : 0
    },
    views: {
      label: 'Views', getter: (row, value: any) => value !== null ? value : 0
    },
    upvotes: {
      label: 'UP Votes', getter: (row, value: any) => value !== null ? value : 0
    },
    downvotes: {
      label: 'Down Votes', getter: (row, value: any) => value !== null ? value : 0
    },
    question: {
      label: 'Question', getter: (row, value: any) => value ? value : ''
    },
    crm_campaign_id: {
      label: 'CRM campaign ID', getter: (row, value: any) => value ? value : ''
    },
    customer_count: {
      label: 'Customers', getter: (row, value: any) => value !== null ? value : 0
    },
    billing_cycle: {
      label: 'Billing Cycle', getter: (row, value: any) => value !== null ? value : 0
    },
    step_name: {
      label: 'Step Impressions', getter: (row, value: any) => value !== null ? value : 0
    },
    input_name: {
      label: 'Input Name', getter: (row, value: any) => value ? value.replace(/<[^>]*>/g, '') : ''
    },
    inputs_count: {
      label: 'Input Count', getter: (row, value: any) => value !== null ? value : 0
    },
    inputs_take_rate: {
      label: 'Inputs take rate', getter: (sessionInput: ReportSessionInput, value: any) => value? `${(value * 100).toFixed(2)}%`: '0.00%'
    },
    autopick: {
      label: 'Autopick', getter: (sessionInput: ReportSessionInput, value: any) => {
        if (value === null) {
          return ''
        }
        return value? 'Yes': 'No'
      }
    },
    is_recurring: {
      label: 'Recurring', getter: (row, value: any) => {
        if (value === null) {
          return ''
        }
        return value? 'Yes': 'No'
      }
    },
    is_trial: {
      label: 'Trial', getter: (row, value: any) => {
        if (value === null) {
          return ''
        }
        return value? 'Yes': 'No'
      }
    },
    upsell_product_name: {
      label: 'Upsell Product', getter: (row, value: any) => value ? value : ''
    },
    order_date: {
      label: 'Order Timestamp', getter: (row, value: any, timezone) => {
        if (!timezone) {
          timezone = moment.tz.guess();
        }
        let thisMoment = moment.tz(value, timezone)
        return thisMoment.format(TimeData.ReportsApiDateFormat);
      }
    },
    customer_name: {
      label: 'Name', getter: (row, value: any) => value ? value : 'Customer not matched'
    },
    session_source: {
      label: 'Session Source', getter: (sessionInput: ReportSessionInput, value: any) => value !== null ? SessionSourceLabels[value] : ''
    },
    message_count: {
      label: 'Message Count', getter: (row, value: any) => value !== null ? value : 0
    },
    chatbot_session_id: {
      label: 'Chatbot Session ID', getter: (row, value: any) => value ? value : ''
    },
    question_at: {
      label: 'Q Time', getter: (row, value: any) => value ? formatTime(value) : ''
    },
    answer_at: {
      label: 'A Time', getter: (row, value: any) => value ? formatTime(value) : ''
    },
    answer: {
      label: 'Bot Answer', getter: (row, value: any) => value ? value : ''
  },
  intents: {
    label: 'Intents', getter: (row, value: number[]) => value && value.length > 0
        ? value.map(intent => ChatbotIntentLabels[intent]).join(', ')
        : ''
  },
  sentiment: {
    label: 'Sentiment', getter: (row, value: any) => value !== null ? ChatbotSentimentLabels[value] : ''
  },
  threat_identified: {
    label: 'Threat Identified', getter: (row, value: any) => value ? 'Yes' : 'No'
  },
    billing_interval_days: {
      label: 'Rebill Interval', getter: (row, value: any) => value ? value : ''
    },
    initial_transaction_date: {
      label: 'Initial Rebill Date', getter: (row, value: any) => value ? value : ''
    },
    rebill_date: {
      label: 'Rebill Date', getter: (row, value: any) => value ? value : ''
    },
    is_order_active: {
      label: 'Order Active', getter: (row, value: any) => {
        if (value === null) {
          return ''
        }
        return value? 'Yes': 'No'
      }
    },
    sms_status: {
      label: 'SMS Status', getter: (row, value: any) => value ? value : ''
    },
    session_type: {
      label: 'Session Type', getter: (row, value: any) => value ? value : ''
    },
    session_action_status: {
      label: 'Session Action Status', getter: (row, value: any) => value ? value : ''
    },
    message: {
      label: 'Message', getter: (row, value: any) => value ? value : ''
    },
    handled_handoff: {
      label: 'Handled by call center', getter: (row, value: any) => value !== null ? value : 0
    },
    handoff: {
      label: 'HandOffs', getter: (row, value: any) => value !== null ? value : 0
    },
    product_price: {
      label: 'Product price', getter: (row, value: any) => convertToUSD(value)
    },
    hold_save: {
      label: 'Saves on Holds', custom_label: 'Saved', getter: (sessionInput: ReportSessionInput, value: any) => value !== null ? value : 0
    },
    subscription_save: {
      label: 'Saves on Subscriptions', custom_label: 'Saved', getter: (sessionInput: ReportSessionInput, value: any) =>value !== null ? value : 0
    },
    rma_save: {
      label: 'Saves on RMAs', custom_label: 'Saved', getter: (sessionInput: ReportSessionInput, value: any) => value !== null ? value : 0
    },
    hold_cancel: {
      label: 'Cancels on Holds', summary_label: 'Cancels Executed', custom_label: 'Cancels Executed', getter: (sessionInput: ReportSessionInput, value: any) => value !== null ? value : 0
    },
    subscription_cancel: {
      label: 'Cancels on Subscriptions', summary_label: 'Cancelled Subscriptions', custom_label:'Cancels Executed', getter: (sessionInput: ReportSessionInput, value: any) => value !== null ? value : 0
    },
    merchant_id: {label: 'Merchant ID', getter: (session: Session, value: any) => value ? value : ''},
    chargeback_ratio: {label: 'Chargeback Ratio', getter: (session: Session, value: any) => value ? value.toFixed(2) + " %" : 0.0},
    dispute_ratio: {label: 'Dispute Ratio', getter: (session: Session, value: any) => value ? value.toFixed(2) + " %" : 0.0},
    alert_ratio: {label: 'Alert Ratio', getter: (session: Session, value: any) => value ? value.toFixed(2) + " %" : 0.0},
    time_period: {label: 'Time Period', getter: (session: Session, value: any) => {
          var date = new Date(value);
          return date.toLocaleString('en-US', { month: 'long' }) + " " + date.getFullYear()
        }
      },
    chargeback_date: {
      label: 'Chargeback Date', getter: (row, value: any, timezone) => {
        if (!timezone) {
          timezone = moment.tz.guess();
        }
        let thisMoment = moment.tz(value, timezone)
        return thisMoment.format(TimeData.ReportsApiDateFormat);
      }
    },
    created: {
      label: 'Transaction Date', getter: (row, value: any, timezone) => {
        if (!timezone) {
          timezone = moment.tz.guess();
        }
        let thisMoment = moment.tz(value, timezone)
        return thisMoment.format(TimeData.ReportsApiDateFormat);
      }
    },
    refund_date: {
      label: 'Refund Date', getter: (row, value: any, timezone) => {
        if (!timezone) {
          timezone = moment.tz.guess();
        }
        let thisMoment = moment.tz(value, timezone)
        return thisMoment.format(TimeData.ReportsApiDateFormat);
      }
    },
    card_bin: {label: 'Card BIN', getter: (session: Session, value: any) => value ? value : ''},
    card_last4: {label: 'Card Last 4', getter: (session: Session, value: any) => value ? value : ''},
    card_type: {label: 'Card Type', getter: (session: Session, value: any) => getCardTypeValue(value)},
    mid_number: {label: 'MID Number', custom_label:'MID', getter: (session: Session, value: any) => value ? value : ''},
    crm_campaign: {
      label: 'CRM campaign', getter: (sessionInput: ReportSessionInput, value: any) => value
    },
    campaign: {
      label: 'Brand', getter: (sessionInput: ReportSessionInput, value: any) => value
    },
    transaction_id: {
      label: 'Txn ID', getter: (sessionInput: ReportSessionInput, value: any) => value
    },
    amount: {
      label: 'Transaction Amount', getter: (sessionInput: ReportSessionInput, value: any) => convertToUSD(value)
    },
    customer_email: {
      label: 'Customer Email', getter: (sessionInput: ReportSessionInput, value: any) => value
    },
    orders_count: {
      label: 'Orders', getter: (row, value: any) => value !== null ? value : 0
    },
    rdr_count: {
      label: 'RDR Count', getter: (row, value: any) => value !== null ? value : 0
    },
    chargeback_count: {
      label: 'Chargeback Count', custom_label: 'CB Count', getter: (row, value: any) => value !== null ? value : 0
    },
    dispute_count: {
      label: 'Dispute Count', getter: (row, value: any) => value !== null ? value : 0
    },
    alert_count: {
      label: 'Alert Count', getter: (row, value: any) => value !== null ? value : 0
    },
    alert_source: {label: 'Alert Source', getter: (session: Session, value: any) => value >= 0 ? AlertSourceLabel[value] : 'None'},
    matched_status: {label: 'Solvpath Matched Status', custom_label: 'Match Type', getter: (session: Session, value: any) => value >= 0 ? TranMatchedStatusLabel[value] : 'None'},
    request_type: {label: 'Request Type', getter: (session: Session, value: any) => value >= 0 ? RequestTypeLabel[value] : 'None'},
    transaction_count: {
      label: 'Transaction Count', custom_label: 'Transactions', getter: (row, value: any) => value !== null ? value : 0
    },
    transaction_type: {
      label: 'Txn Type',
      getter: (row, value: any) => {
        let result = 'Unknown';
        if (typeof value === 'string') {
          result = value;
        } else if (value >= 0) {
          result = TranTypeLabel[value];
        }
        return result;
      }
    },
    sp_session_source: {label: 'Session Source', getter: (session: Session, value: any) => SessionSourceLabels[value]},
    sp_session_id: {
      label: 'Solvpath Session ID', getter: (session: Session, value: any) =>
        value ? value : ''
    },
    payment_source: {
      label: 'Payment Method',
      getter: (row, value: any) => (row['payment_source'] >= 0) ? TransactionPaymentSourceLabel[row['payment_source']] : 'Unknown'
    },
    successful_session: {
      label: 'Successful', getter: (row, value: any) => value !== null ? value : 0
    },
    dropoff: {
      label: 'Dropoff', getter: (row, value: any) => value !== null ? value : 0
    },
    dropoffs: {
      label: 'Dropoffs', getter: (row, value: any) => value !== null ? value : 0
    },
    unmatched: {
      label: 'Unmatched', getter: (row, value: any) => value !== null ? value : 0
    },
    update_info: {
      label: 'Update Info', getter: (row, value: any) => value !== null ? value : ''
    },
    faqs: {
      label: 'FAQs', getter: (row, value: any) => value !== null ? value : ''
    },
    marketplace: {
      label: 'Marketplace', getter: (row, value: any) => value !== null ? value : ''
    },
    successfull_sessions: {
      label: 'Successfull Sessions', getter: (row, value: any) => value !== null ? value : 0
    },
    self_service_rate: {
      label: 'Self Service Rate', getter: (sessionInput: ReportSessionInput, value: any) => value? `${(value * 100).toFixed(2)}%`: '0.00%'
    },
    step_category: {
      label: 'Step Category', getter: (row, value: any) => value !== null ? value : ''
    },
    total_saves: {
      label: 'Total Saves', getter: (row, value: any) => value !== null ? value : 0
    },
    retracted_save: {
      label: 'Retracted Saves', getter: (row, value: any) => value !== null ? value : 0
    },
    total_views: {
      label: 'Total Views', getter: (row, value: any) => value !== null ? value : 0
    },
    total_add_to_carts: {
      label: 'Total Add to carts', getter: (row, value: any) => value !== null ? value : 0
    },
    total_checked_outs: {
      label: 'Total Checked outs', getter: (row, value: any) => value !== null ? value : 0
    },
    total_revenue: {
      label: 'Revenue', getter: (sessionInput: ReportSessionInput, value: any) => convertToUSD(value)
    },
    total_sale: {
      label: 'Total Sales', getter: (row, value: any) => value !== null ? value : 0
    },
    cancel_hold_request: {
      label: 'Cancel on hold requests', summary_label: 'Cancel Requests', custom_label: 'Cancel Requests', getter: (row, value: any) => value !== null ? value : 0
    },
    cancel_subscription_request: {
      label: 'Cancel on subscription requests', custom_label: 'Cancel Requests', getter: (row, value: any) => value !== null ? value : 0
    },
    rma_request: {
      label: 'Return Requests', custom_label: 'Return Requests', getter: (row, value: any) => value !== null ? value : 0
    },
    subscription_adjustment: {
      label: 'Subscription Adjustments', getter: (row, value: any) => value !== null ? value : 0
    },
    save_hold_rate: {
      label: 'Save Rate', custom_label: 'Retention Rate', getter: (sessionInput: ReportSessionInput, value: any) => value? `${(value * 100).toFixed(2)}%`: '0.00%'
    },
    save_subscription_rate: {
      label: 'Save Rate', custom_label:'Retention Rate', getter: (sessionInput: ReportSessionInput, value: any) => value? `${(value * 100).toFixed(2)}%`: '0.00%'
    },
    rma_save_rate: {
      label: 'Save Rate', custom_label:'Retention Rate', getter: (sessionInput: ReportSessionInput, value: any) => value? `${(value * 100).toFixed(2)}%`: '0.00%'
    },
    subscription_reactivate: {
      label: 'Subscription Reactivates', custom_label:'Reactivations', getter: (row, value: any) => value !== null ? value : 0
    },
    reorder: {
      label: 'Reorder', getter: (row, value: any) => value !== null ? value : 0
    },
    conversion_based_on_visits: {
      label: 'Visits Conversion', getter: (row, value: any) => value !== null ? `${(value * 100).toFixed(2)}%`: '0.00%'
    },
    conversion_based_on_add_to_cart: {
      label: 'Interested Buyers Conversion', getter: (row, value: any) => value !== null ? `${(value * 100).toFixed(2)}%`: '0.00%'
    },
    payment_method: {
      label: 'Payment Method', getter: (row, value: any) => value !== null ? value : ""
    },
    total_buy_nows: {
      label: 'Total Buy nows', getter: (row, value: any) => value !== null ? value : 0
    },
    total_marketplace_views: {
      label: 'Visitors', getter: (row, value: any) => value !== null ? value : 0
    },
    total_quantity: {
      label: 'Sales', getter: (row, value: any) => value !== null ? value : 0
    },
    conversion_based_on_marketplace_views: {
      label: 'Conversion', getter: (row, value: any) => value !== null ? `${(value * 100).toFixed(2)}%`: '0.00%'
    },
    conversion_based_on_views: {
      label: 'Conversion', getter: (row, value: any) => value !== null ? `${(value * 100).toFixed(2)}%`: '0.00%'
    },
    average_order_value: {
      label: 'AOV', getter: (sessionInput: ReportSessionInput, value: any) => convertToUSD(value)
    },
    watchlist: {
      label: 'Watch list', getter: (row, value: any) => value !== null ? value : ""
    },
    revise_request: {
      label: 'Revise requests', getter: (row, value: any) => value !== null ? value : 0
    },
    shipping_frequency_request: {
      label: 'Shipping Frequency requests', getter: (row, value: any) => value !== null ? value : 0
    },
    order_status_request: {
      label: 'Order Status requests', getter: (row, value: any) => value !== null ? value : 0
    },
    update_info_request: {
      label: 'Update Info requests', getter: (row, value: any) => value !== null ? value : 0
    },
    faqs_request: {
      label: 'FAQs requests', getter: (row, value: any) => value !== null ? value : 0
    },
    marketplace_request: {
      label: 'Marketplace requests', getter: (row, value: any) => value !== null ? value : 0
    },
    error: {
      label: 'Error', getter: (row, value: any) => value !== null ? value : ""
    },
    note: {
      label: 'CRM Note', getter: (row, value: any) => value !== null ? value : ""
    },
    email: {
      label: 'Customer Email', getter: (row, value: any) => value !== null ? value : ""
    },
    phone: {
      label: 'Customer Phone', getter: (row, value: any) => value !== null ? value : ""
    },
    path_name: {
      label: 'Path Name', getter: (row, value: any) => value !== null ? value : ""
    },
    errors_count: {
      label: 'Total Failures', custom_label: 'Errors', getter: (row, value: any) => value !== null ? value : 0
    },
    converted: {
      label: 'Total Converted', summary_label:'Conversions', custom_label:'Success', getter: (row, value: any) => value !== null ? value : 0
    },
    conversion_rate: {
      label: 'Conversion Rate', custom_label: 'Conversion Rate', getter: (row, value: any) => value !== null ? `${(value * 100).toFixed(2)}%`: '0.00%'
    },
    total_cart_abandoned: {
      label: 'Total Cart Abandoned', getter: (row, value: any) => value !== null ? value : 0
    },
    total_card_declined: {
      label: 'Total Card Declined', getter: (row, value: any) => value !== null ? value : 0
    },
    failed_tries: {
      label: 'Failed Tries', getter: (row, value: any) => value !== null ? value : 0
    },
    ip_address: {
      label: 'IP Address', getter: (row, value: any) => value !== null ? value : ""
    },
    clicks: {
      label: 'Clicks', getter: (row, value: any) => value !== null ? value : 0
    },
    declined: {
      label: 'Declines', getter: (row, value: any) => value !== null ? value : 0
    },
    potential_revenue: {
      label: 'Potential Revenue', getter: (row, value: any) => convertToUSD(value)
    },
    sp_session_path: {
      label: 'Product Path',
      getter: (row, value: any) => value ? value : ''
    },
    sp_session_step: {
      label: 'Step', getter: (row, value: any) => value ? value : ''
    },
    sp_session_status: {label: 'Session Status',
      getter: (row, value: any) => value ? value : ''},
    sp_session_result: {
      label: 'Session Result',
      getter: (row, value: any) => value ? value : ''
    },
    sp_session_latest_result: {
      label: 'Latest Result',
      getter: (row, value: any) => value ? value : ''
    },
    sp_session_steps_count: {
      label: 'Steps Count', getter: (row, value: any) => value !== null ? value : 0
    },
    recurring_revenue: {
      label: 'Recurring Revenue', custom_label:'MRR', getter: (row, value: any) => convertToUSD(value)
    },
    save_ratio: {
      label: 'Save Ratio', getter: (sessionInput: ReportSessionInput, value: any) => value? `${(value * 100).toFixed(2)}%`: '0.00%'
    },
    refund_ratio: {
      label: 'Refund Ratio', getter: (sessionInput: ReportSessionInput, value: any) => value? `${(value * 100).toFixed(2)}%`: '0.00%'
    },
    avg_refund_amount: {
      label: 'Average Refund Amount', custom_label:'Average Refund Amount', getter: (sessionInput: ReportSessionInput, value: any) => convertToUSD(value)
    },
    sessions: {
      label: 'Sessions', getter: (row, value: any) => value !== null ? value : 0
    },
    item_price: {
      label: 'Total Billed', custom_label: 'Total Billed', getter: (row, value: any) => convertToUSD(value)
    },
    item_save_amount: {
      label: 'Save Amount', summary_label: 'Revenue Saved', custom_label: 'Saved', getter: (row, value: any) => convertToUSD(value)
  },
  item_refund_amount: {
    label: 'Refund amount', custom_label: 'Refund', getter: (sessionInput: ReportSessionInput, value: any) => convertToUSD(value)
  },
  item_full_refund_amount: {
    label: 'Full refund amount', custom_label: 'Full Refund', getter: (sessionInput: ReportSessionInput, value: any) => convertToUSD(value)
  },
  brand_name: {
    label: 'Brand Name', getter: (row, value: any) => value ? value : ''
  },
  merchant_organization_id: {
    label: 'Merchant Organization ID', getter: (row, value: any) => value ? value : ''
  },
  gateway_id: {
    label: 'Gateway ID', getter: (row, value: any) => value ? value : ''
  },
  acquirer_bank_name: {
    label: 'Acquirer Bank Name', getter: (row, value: any) => value ? value : ''
  },
  processor_type: {
    label: 'Processor', getter: (row, value: any) => value ? value : ''
  },
  transaction_status: {
    label: 'Status', getter: (row, value: any) => value ? value : ''
  },
  transaction_amount: {
    label: 'Amount', getter: (row, value: any) => convertToUSD(value)
  },
  transaction_attempted_date: {
    label: 'Txn Date & Time', getter: (row, value: any) => formatDateTimeEST(value)
  },
  issuer_bank_name: {
    label: 'Issuer Bank Name', getter: (row, value: any) => value ? value : ''
  },
  reason: {
    label: 'Reason', getter: (row, value: any) => value ? value : ''
  },
  reason_code: {
    label: 'Reason Code', getter: (row, value: any) => value ? value : ''
  },
  response_code: {
    label: 'Response Code', getter: (row, value: any) => value ? value : ''
  },
  decline_source: {
    label: 'Decline Source', getter: (row, value: any) => value ? value : ''
  },
  pares_status: {
    label: 'PARes Status', getter: (row, value: any) => value ? value : ''
  },
  three_ds_status: {
    label: '3DS Status', getter: (row, value: any) => value ? value : ''
  },
  veres_enrolled: {
    label: 'VERes Enrolled', getter: (row, value: any) => value ? value : ''
  },
  three_ds_enabled: { label: '3DS Enabled', getter: (row, value: any) => value ? value : '' },
  account_id: { label: 'Account ID', getter: (row, value: any) => value ? value : '' },
  campaign_id: { label: 'Brand ID', getter: (row, value: any) => value ? value : '' },
  parent_transaction_id: { label: 'Parent Txn ID', getter: (row, value: any) => value ? value : '' },
  merchant_category_code: { label: 'MCC', getter: (row, value: any) => value ? value : '' },
  transaction_success_date: { label: 'Success Date & Time', getter: (row, value: any) => formatDateTimeEST(value) },
  gateway_name: { label: 'Gateway', getter: (row, value: any) => value ? value : '' },
  gateway_type: { label: 'Gateway Type', getter: (row, value: any) => value ? value : '' },
  terminal_number: { label: 'Terminal #', getter: (row, value: any) => value ? value : '' },
  client_ref_id: { label: 'Client Ref ID', getter: (row, value: any) => value ? value : '' },
  transacting_mid_organization_id: {
    label: 'Transacting MID Org ID',
    getter: (row, value: any) => value ? value : ''
  },
  acquirer_bin: { label: 'Acquirer BIN', getter: (row, value: any) => value ? value : '' },
  avs_code: { label: 'AVS Code', getter: (row, value: any) => value ? value : '' },
  avs_code_value: { label: 'AVS Code Value', getter: (row, value: any) => value ? value : '' },
  avs_code_raw: { label: 'AVS Code Raw', getter: (row, value: any) => value ? value : '' },
  instrument_identifier_id: {
    label: 'Instrument Identifier ID',
    getter: (row, value: any) => value ? value : ''
  },
  payment_instrument_id: {
    label: 'Payment Instrument ID',
    getter: (row, value: any) => value ? value : ''
  },
  action: { label: 'Action', getter: (row, value: any) => value ? value : '' },
  response_code_value: { label: 'Response Code Value', getter: (row, value: any) => value ? value : '' },
  vc_correlation_id: {
    label: 'VC Correlation ID',
    getter: (row, value: any) => value ? value : ''
  },
  ecommerce_indicator: {
    label: 'E-commerce Indicator',
    getter: (row, value: any) => value ? value : ''
  },
  reconciliation_id: {
    label: 'Reconciliation ID',
    getter: (row, value: any) => value ? value : ''
  },
  customer_first_name: { label: 'Cust First Name', getter: (row, value: any) => value ? value : '' },
  customer_last_name: { label: 'Cust Last Name', getter: (row, value: any) => value ? value : '' },

  card_last_four: { label: 'Card Last Four', getter: (row, value: any) => value ? value : '' },

  billing_name: { label: 'Billing Name', getter: (row, value: any) => value ? value : '' },
  billing_email: { label: 'Billing Email', getter: (row, value: any) => value ? value : '' },
  billing_address: { label: 'Billing Address', getter: (row, value: any) => value ? value : '' },
  billing_phone: { label: 'Billing Phone', getter: (row, value: any) => value ? value : '' },

  shipping_email: { label: 'Shipping Email', getter: (row, value: any) => value ? value : '' },
  shipping_address: { label: 'Shipping Address', getter: (row, value: any) => value ? value : '' },

  network_transaction_id: {
    label: 'Network Txn ID',
    getter: (row, value: any) => value ? value : ''
  },
  approval_code: { label: 'Approval Code', getter: (row, value: any) => value ? value : '' },

  tax_amount: { label: 'Tax Amount', getter: (row, value: any) => convertToUSD(value) },
  authorized_amount: { label: 'Authorized Amount', getter: (row, value: any) => convertToUSD(value) },

  ach_verification_code: {
    label: 'ACH Verification Code',
    getter: (row, value: any) => value ? value : ''
  },
  ach_verification_code_value: {
    label: 'ACH Verification Code Value',
    getter: (row, value: any) => value ? value : ''
  },
  ach_verification_code_raw: {
    label: 'ACH Verification Code Raw',
    getter: (row, value: any) => value ? value : ''
  },
  host_name: { label: 'Host Name', getter: (row, value: any) => value ? value : '' },
  cvv_result_code: { label: 'CVV Result Code', getter: (row, value: any) => value ? value : '' },
  cvv_result_code_value: { label: 'CVV Result Code Value', getter: (row, value: any) => value ? value : '' },
  cvv_result_code_raw: { label: 'CVV Result Code Raw', getter: (row, value: any) => value ? value : '' },
  cdn_installed: { label: 'CDN Installed', getter: (row, value: any) => value ? 'Yes' : 'No' },
  is_rebill_transaction: { label: 'Is Rebill Txn', getter: (row, value: any) => value ? 'Yes' : 'No' },
  customer_vault_id: { label: 'Customer Vault ID', getter: (row, value: any) => value ? value : '' },
  issuer_country: { label: 'Issuer Country', getter: (row, value: any) => value ? value : '' },
  eci_raw_value: { label: 'ECI Raw Value', getter: (row, value: any) => value ? value : '' },
  directory_server_error_code: { label: 'Directory Server Error Code', getter: (row, value: any) => value ? value : '' },
  directory_server_error_description: { label: 'Directory Server Error Description', getter: (row, value: any) => value ? value : '' },
  ach_verification_code_raw_value: { label: 'ACH Verification Code Raw Value', getter: (row, value: any) => value ? value : '' },
  source_url: { label: 'Source URL', getter: (row, value: any) => value ? value : '' },
  rma: {
    label: 'Return quantity', custom_label: 'Returns Executed', getter: (sessionInput: ReportSessionInput, value: any) => value !== null ? value : 0
  },
  order_hold_save: {
    label: 'Saves on Holds', custom_label: 'Orders Saved', getter: (sessionInput: ReportSessionInput, value: any) => value !== null ? value : 0
  },
  order_subscription_save: {
    label: 'Saves on Subscriptions', custom_label: 'Subscriptions Saved', getter: (sessionInput: ReportSessionInput, value: any) =>value !== null ? value : 0
  },
  order_rma_save: {
    label: 'Saves on RMAs', custom_label: 'RMA Saved', getter: (sessionInput: ReportSessionInput, value: any) => value !== null ? value : 0
  },
  order_hold_cancel: {
    label: 'Cancels on Holds', summary_label: 'Cancels Executed', custom_label: 'Cancels Executed', getter: (sessionInput: ReportSessionInput, value: any) => value !== null ? value : 0
  },
  order_subscription_cancel: {
    label: 'Cancels on Subscriptions', summary_label: 'Cancelled Subscriptions', custom_label:'Cancels Executed', getter: (sessionInput: ReportSessionInput, value: any) => value !== null ? value : 0
  },
  order_rma: {
    label: 'Return quantity', custom_label: 'Returns Executed', getter: (sessionInput: ReportSessionInput, value: any) => value !== null ? value : 0
  },
  order_cancel_hold_request: {
    label: 'Cancel on hold requests', summary_label: 'Cancel Requests', custom_label: 'Cancel Requests', getter: (row, value: any) => value !== null ? value : 0
  },
  order_cancel_subscription_request: {
    label: 'Cancel on subscription requests', custom_label: 'Cancel Requests', getter: (row, value: any) => value !== null ? value : 0
  },
  order_rma_request: {
    label: 'Return Requests', custom_label: 'Return Requests', getter: (row, value: any) => value !== null ? value : 0
  },
  order_save_hold_rate: {
    label: 'Save Rate', custom_label: 'Retention Rate', getter: (sessionInput: ReportSessionInput, value: any) => value? `${(value * 100).toFixed(2)}%`: '0.00%'
  },
  order_save_subscription_rate: {
    label: 'Save Rate', custom_label:'Retention Rate', getter: (sessionInput: ReportSessionInput, value: any) => value? `${(value * 100).toFixed(2)}%`: '0.00%'
  },
  order_save_rma_rate: {
    label: 'Save Rate', custom_label:'Retention Rate', getter: (sessionInput: ReportSessionInput, value: any) => value? `${(value * 100).toFixed(2)}%`: '0.00%'
  },
  hold_intent: {
    label: 'Offer Intent', getter: (sessionInput: ReportSessionInput, value: any) => value ? value : ''
  },
  subscription_intent: {
    label: 'Offer Intent', getter: (sessionInput: ReportSessionInput, value: any) => value ? value : ''
  },
  rma_intent: {
    label: 'Offer Intent', getter: (sessionInput: ReportSessionInput, value: any) => value ? value : ''
  },
  last_hold_step: {
    label: 'Step Name', getter: (row, value: any) => value !== null ? value : ''
  },
  last_subscription_step: {
    label: 'Step Name', getter: (row, value: any) => value !== null ? value : ''
  },
  last_rma_step: {
    label: 'Step Name', getter: (row, value: any) => value !== null ? value : ''
  },
  last_hold_input: {
    label: 'Input Name', getter: (row, value: any) => value !== null ? value : ''
  },
  last_subscription_input: {
    label: 'Input Name', getter: (row, value: any) => value !== null ? value : ''
  },
  last_rma_input: {
    label: 'Input Name', getter: (row, value: any) => value !== null ? value : ''
  },
  cancel_hold_request_abandoned: {
    label: 'Abandoned', getter: (row, value: any) => value !== null ? value : ''
  },
  cancel_subscription_request_abandoned: {
    label: 'Abandoned', getter: (row, value: any) => value !== null ? value : ''
  },
  rma_request_abandoned: {
    label: 'Abandoned', getter: (row, value: any) => value !== null ? value : ''
  },
  cancel_reason_category: {
    label: 'Cancel Reason Category', getter: (row, value: any) => value !== null ? value : ''
  },
  impressions: {
    label: 'Impressions', getter: (row, value: any) => value !== null ? value : 0
  },
  hold_saves: {
    label: 'Hold Saves', getter: (row, value: any) => value !== null ? value : 0
  },
  subscription_saves: {
    label: 'Subscription Saves', getter: (row, value: any) => value !== null ? value : 0
  },
  rma_saves: {
    label: 'RMA Saves', getter: (row, value: any) => value !== null ? value : 0
  },
}

export const reportsFieldDataV3 = {
  // General

  // Composite UI key — expanded to base_product_name + base_product_id at query time
  base_product_name_id: {
    label: 'Base Product (Name + ID)',
    custom_label: 'Base Product',
    getter: (row: any, _value: any) => {
      let result = '--';
      if (row['base_product_name']) {
        result = row['base_product_name'];
      }
      if (row['base_product_id']) {
        result = result !== '--' ? result + ' (' + row['base_product_id'] + ')' : String(row['base_product_id']);
      }
      return result;
    },
  },
  // Composite UI key — expanded to product_name + product_id at query time
  product_name_id: {
    label: 'Product (Name + ID)',
    custom_label: 'Camp Product',
    getter: (row: any, _value: any) => {
      let result = '--';
      if (row['product_name']) {
        result = row['product_name'];
      }
      if (row['product_id']) {
        result = result ? result + ' (' + row['product_id'] + ')' : String(row['product_id']);
      }
      return result;
    },
  },
  product_name: {
    label: 'Product Name',
    custom_label: 'Product',
    getter: (_row: any, value: any) => (value ? value : '--'),
  },
  product_id: {
    label: 'Product ID',
    getter: (_row: any, value: any) => (value ? value : '--'),
  },
  product_variant: {
    label: 'Product Variant',
    custom_label: 'Prod Var',
    getter: (_row: any, value: any) => (value ? value : '--'),
  },
  crm_name: {
    label: 'CRM Name',
    custom_label: 'Platform',
    getter: (_row: any, value: any) => (value ? value : '--'),
  },
  // Composite UI key — expanded to crm_campaign_name + crm_campaign_id at query time
  crm_campaign_name_id: {
    label: 'CRM Campaign',
    custom_label: 'Campaign',
    getter: (row: any, _value: any) => {
      let result = '--';
      if (row['crm_campaign_name']) {
        result = row['crm_campaign_name'];
      }
      if (row['crm_campaign_id']) {
        result = result !== '--' ? result + ' (' + row['crm_campaign_id'] + ')' : String(row['crm_campaign_id']);
      }
      return result;
    },
  },
  // Composite UI key — expanded to campaign_name + campaign_id at query time
  campaign_name_id: {
    label: 'Campaign (ID + Name)',
    custom_label: 'Campaign',
    getter: (row: any, _value: any) => {
      let result = '--';
      if (row['campaign_name']) {
        result = row['campaign_name'];
      }
      if (row['campaign_id']) {
        result = result ? result + ' (' + row['campaign_id'] + ')' : String(row['campaign_id']);
      }
      return result;
    },
  },
  campaign_name: {
    label: 'Campaign Name',
    custom_label: 'Brand',
    getter: (_row: any, value: any) => (value ? value : '--'),
  },
  campaign_id: {
    label: 'Campaign ID',
    getter: (_row: any, value: any) => (value ? value : '--'),
  },
  cancel_reason: {
    label: 'Cancel Reason',
    custom_label: 'Reason',
    getter: (_row: any, value: any) => (value ? value : 'Unknown'),
  },
  cancel_reason_category_name: {
    label: 'Cancel Reason Category Name',
    custom_label: 'Reason Category',
    getter: (_row: any, value: any) => (value ? value : '--'),
  },
  billing_cycle: {
    label: 'Billing Cycle',
    custom_label: 'Cycle',
    getter: (_row: any, value: any) => (value !== null ? value : 0),
  },
  billing_cycle_group: {
    label: 'Billing Cycle Group',
    custom_label: 'Cycle Grp',
    getter: (_row: any, value: any) => (value !== null && value !== undefined ? String(value) : '--'),
  },
  subscription_path: {
    label: 'Product Path',
    custom_label: 'Path',
    getter: (_row: any, value: any) => (value ? value : '--'),
  },

  // Traffic
  tracking_source: {
    label: 'Traffic Source',
    custom_label: 'SRC',
    getter: (_row: any, value: any) => (value ? value : '--'),
  },
  aff_id: {
    label: 'Affiliate ID',
    custom_label: 'AFID',
    getter: (_row: any, value: any) => (value ? value : '--'),
  },
  sub_id_1: {
    label: 'Sub ID',
    custom_label: 'SubID',
    getter: (_row: any, value: any) => (value ? value : '--'),
  },
  sub_id_2: {
    label: 'Sub ID 2',
    custom_label: 'SubID2',
    getter: (_row: any, value: any) => (value ? value : '--'),
  },
  sub_id_3: {
    label: 'Sub ID 3',
    custom_label: 'SubID3',
    getter: (_row: any, value: any) => (value ? value : '--'),
  },

  // Device
  device_type: {
    label: 'Device Type',
    custom_label: 'Device',
    getter: (_row: any, value: any) => (value ? value : '--'),
  },
  os_name: {
    label: 'Operating System',
    custom_label: 'OS',
    getter: (_row: any, value: any) => (value ? value : '--'),
  },
  browser_name: {
    label: 'Browser',
    custom_label: 'Brwsr',
    getter: (_row: any, value: any) => (value ? value : '--'),
  },
  device_brand: {
    label: 'Device Brand',
    custom_label: 'MFR',
    getter: (_row: any, value: any) => (value ? value : '--'),
  },

  // Detailed View
  customer_id: {
    label: 'Customer ID',
    custom_label: 'CustID',
    getter: (row: any, value: any) => getCRMCustomerHtml(value, row.crm_type),
  },
  order_id: {
    label: 'Order ID',
    custom_label: 'OrdID',
    getter: (row: any, value: any) => getCRMOrderHtml(value, value, row.crm_type),
  },
  sp_session_id: {
    label: 'Session ID',
    custom_label: 'SessID',
    getter: (_row: any, value: any) => (value ? value : '--'),
  },
  session_id: {
    label: 'Session ID',
    custom_label: 'SessID',
    getter: (_row: any, value: any) => (value ? value : '--'),
  },

  // Detailed View - Subscription
  // Metrics
  session_count: {
    label: 'Sessions',
    getter: (_row: any, value: any) => (value !== null ? value : 0),
  },
  cancel_subscription_request: {
    label: 'Cancel Requests',
    custom_label: 'Cancel Req',
    summaryHelpText: 'Number of customer-initiated cancel sessions.',
    getter: (_row: any, value: any) => (value !== null ? value : 0),
  },
  subscription_cancel: {
    label: 'Cancels Executed',
    custom_label: 'Subs Cancelled',
    pct_label: 'Subs Cancelled (%)',
    getter: (row: any, value: any) => {
      let result: any = value !== null ? value : 0;
      if (row['subscription_cancel_pct'] !== null && row['subscription_cancel_pct'] !== undefined) {
        result = String(result) + '&ensp;(' + formatPct(row['subscription_cancel_pct']) + ')';
      }
      return result;
    },
  },
  subscription_save: {
    label: 'Saved',
    custom_label: 'Subs Saved',
    pct_label: 'Subs Saved (%)',
    summaryHelpText:
      'Percentage of cancel requests where the subscription remains active. Active subscriptions ÷ Cancel Requests',
    getter: (row: any, value: any) => {
      let result: any = value !== null ? value : 0;
      if (row['subscription_save_pct'] !== null && row['subscription_save_pct'] !== undefined) {
        result = String(result) + '&ensp;(' + formatPct(row['subscription_save_pct']) + ')';
      }
      return result;
    },
  },
  amount_paid: {
    label: 'Total Billed',
    getter: (_row: any, value: any) => convertToUSD(value),
  },
  product_role: {
    label: 'Product Role',
    custom_label: 'Role',
    getter: (_row: any, value: any) => (value ? value : '--'),
  },
  item_refund_amount: {
    label: 'Refund Amount $',
    getter: (_row: any, value: any) => convertToUSD(value),
  },
  save_subscription_rate: {
    label: 'Retention Rate',
    custom_label: 'Retention Rate %',
    summaryHelpText:
      'Measures how often subscriptions are retained after a cancel request. Subscriptions Saved ÷ Cancel Requests',
    getter: (_row: any, value: any) => formatPct(value),
  },
  cancel_subscription_request_abandoned: {
    label: 'Abandoned',
    summaryHelpText: 'Cancel sessions started but not completed by the customer.',
    getter: (row: any, value: any) => {
      let result: any = value !== null ? value : 0;
      if (
        row['cancel_subscription_request_abandoned_pct'] !== null &&
        row['cancel_subscription_request_abandoned_pct'] !== undefined
      ) {
        result = String(result) + '&ensp;(' + formatPct(row['cancel_subscription_request_abandoned_pct']) + ')';
      }
      return result;
    },
  },

  // Offer Group Fields - Subscription & Order Both
  offer_type: {
    label: 'Offer Type',
    getter: (_row: any, value: any) => (value ? StepCategoryLabels[value] : '--'),
  },
  offer_name: {
    label: 'Offer Name',
    getter: (row: any, value: any) => {
      const name = value ? value : '--';
      const id = row && row['offer_id'] ? row['offer_id'] : null;
      return id ? `(${id}) ${name}` : name;
    },
  },
  position: {
    label: 'Position',
    getter: (_row: any, value: any) => (value ? value : '--'),
  },
  offer_position: {
    label: 'Position',
    getter: (row: any, value: any) => {
      let result = value ? value : '--';
      let funnelType;
      if (row['subscription_funnel_type'] !== undefined) {
        funnelType = row['subscription_funnel_type'];
      } else if (row['hold_funnel_type'] !== undefined) {
        funnelType = row['hold_funnel_type'];
      } else {
        funnelType = row['rma_funnel_type'];
      }
      if (funnelType !== undefined && Number(funnelType) === OfferTypes.Legacy) {
        result = '--';
      }
      return result;
    },
  },
  impressions: {
    label: 'Impressions',
    getter: (_row: any, value: any) => (value !== null ? value : 0),
  },
  total_rejections: {
    label: 'Rejected',
    getter: (_row: any, value: any) => value,
  },
  accepted: {
    label: 'Accepted',
    getter: (_row: any, value: any) => value,
  },
  total_accepted: {
    label: 'Accepted',
    getter: (_row: any, value: any) => value,
  },
  total_impressions: {
    label: 'Impressions',
    getter: (_row: any, value: any) => value,
  },
  accepted_pct: {
    label: 'Accepted %',
    getter: (_row: any, value: any) => formatPct(value),
  },
  avg_mrr_saved: {
    label: 'Avg MRR Saved',
    getter: (_row: any, value: any) => convertToUSD(value),
  },
  revenue_saved: {
    label: 'Revenue Saved $',
    getter: (_row: any, value: any) => convertToUSD(value),
  },

  // Metrics - New Order Retention
  cancel_hold_request: {
    label: 'Cancel Requests',
    summaryHelpText: 'Number of customer-initiated cancel sessions.',
    getter: (_row: any, value: any) => (value !== null ? value : 0),
  },
  item_price: {
    label: 'Billed',
    custom_label: 'Billed $',
    summaryHelpText: 'Total dollar value of items associated with cancel requests, calculated before refunds.',
    getter: (_row: any, value: any) => convertToUSD(value),
  },
  hold_cancel: {
    label: 'Items Cancelled',
    pct_label: 'Items Cancelled (%)',
    getter: (row: any, value: any) => {
      let result: any = value !== null ? value : 0;
      if (row['hold_cancel_pct'] !== null && row['hold_cancel_pct'] !== undefined) {
        result = String(result) + '&ensp;(' + formatPct(row['hold_cancel_pct']) + ')';
      }
      return result;
    },
  },
  hold_save: {
    label: 'Items Saved',
    pct_label: 'Items Saved (%)',
    getter: (row: any, value: any) => {
      let result: any = value !== null ? value : 0;
      if (row['hold_save_pct'] !== null && row['hold_save_pct'] !== undefined) {
        result = String(result) + '&ensp;(' + formatPct(row['hold_save_pct']) + ')';
      }
      return result;
    },
  },
  save_hold_rate: {
    label: 'Retention Rate',
    custom_label: 'Retention Rate %',
    summaryHelpText: 'Percentage of cancel requests where the hero product was saved.\nHero saved ÷ Cancel Requests',
    getter: (_row: any, value: any) => formatPct(value),
  },
  cancel_hold_request_abandoned: {
    label: 'Abandoned',
    summaryHelpText: 'Cancel sessions started but not completed by the customer.',
    getter: (row: any, value: any) => {
      let result: any = value !== null ? value : 0;
      if (row['cancel_hold_request_abandoned_pct'] !== null && row['cancel_hold_request_abandoned_pct'] !== undefined) {
        result = String(result) + '&ensp;(' + formatPct(row['cancel_hold_request_abandoned_pct']) + ')';
      }
      return result;
    },
  },
  product_path: {
    label: 'Customer Stage',
    custom_label: 'Stage',
    getter: (_row: any, value: any) => (value ? value : '--'),
  },
  hold_path: {
    label: 'Product Path',
    custom_label: 'Path',
    getter: (_row: any, value: any) => (value ? value : '--'),
  },
  billing_interval_days: {
    label: 'Frequency',
    getter: (_row: any, value: any) => formatBillingIntervalDays(value),
  },
  input_name: {
    label: 'Input Name',
    getter: (_row: any, value: any) => (value ? value : '--'),
  },
  offer_intent: {
    label: 'Customer Stage',
    custom_label: 'Stage',
    getter: (_row: any, value: any) => (value !== null && value !== undefined ? abbrOfferStageLabel[value] : '--'),
  },
  subscription_audience_name: {
    label: 'Segment',
    custom_label: 'Segment',
    getter: (_row: any, value: any) => (value ? value : '--'),
  },
  hold_audience_name: {
    label: 'Segment',
    custom_label: 'Segment',
    getter: (_row: any, value: any) => (value ? value : '--'),
  },
  save_amount: {
    label: 'Revenue Saved',
    custom_label: 'Saved $',
    getter: (_row: any, value: any) => convertToUSD(value),
  },
  item_full_refund_amount: {
    label: 'Full Refund Amount',
    custom_label: 'Full Refund $',
    getter: (_row: any, value: any) => convertToUSD(value),
  },
  refund_quantity: {
    label: 'Refund Quantity',
    custom_label: 'Refund Qty',
    getter: (_row: any, value: any) => (value !== null ? value : 0),
  },
  item_partial_refund_amount: {
    label: 'Partial Refund Amount',
    custom_label: 'Partial Refund $',
    getter: (_row: any, value: any) => convertToUSD(value),
  },

  // Metrics - Return Retention
  rma_path: {
    label: 'Stage',
    custom_label: 'Stage',
    getter: (_row: any, value: any) => (value ? value : '--'),
  },
  rma_request: {
    label: 'Return Requests',
    custom_label: 'Return Req',
    summaryHelpText: 'Number of customer-initiated return sessions.',
    getter: (_row: any, value: any) => (value !== null ? value : 0),
  },
  rma: {
    label: 'Returns Initiated',
    custom_label: 'Returns',
    pct_label: 'Returns Initiated (%)',
    getter: (row: any, value: any) => {
      let result: any = value !== null ? value : 0;
      if (row['rma_pct'] !== null && row['rma_pct'] !== undefined) {
        result = String(result) + '&ensp;(' + formatPct(row['rma_pct']) + ')';
      }
      return result;
    },
  },
  rma_save: {
    label: 'Deflected',
    custom_label: 'Deflected',
    pct_label: 'Deflected (%)',
    getter: (row: any, value: any) => {
      let result: any = value !== null ? value : 0;
      if (row['rma_save_pct'] !== null && row['rma_save_pct'] !== undefined) {
        result = String(result) + '&ensp;(' + formatPct(row['rma_save_pct']) + ')';
      }
      return result;
    },
  },
  save_rma_rate: {
    label: 'Deflection Rate',
    custom_label: 'Deflection Rate %',
    summaryHelpText: 'Percentage of return requests that were deflected.',
    getter: (_row: any, value: any) => formatPct(value),
  },
  rma_request_abandoned: {
    label: 'Abandoned',
    summaryHelpText: 'Return sessions started but not completed by the customer.',
    getter: (row: any, value: any) => {
      let result: any = value !== null ? value : 0;
      if (
        row['rma_request_abandoned_pct'] !== null &&
        row['rma_request_abandoned_pct'] !== undefined
      ) {
        result = String(result) + '&ensp;(' + formatPct(row['rma_request_abandoned_pct']) + ')';
      }
      return result;
    },
  },
  rma_audience_name: {
    label: 'Segment',
    custom_label: 'Segment',
    getter: (_row: any, value: any) => (value ? value : '--'),
  },
  label_created: {
    label: 'Labels Created',
    custom_label: 'Labels',
    getter: (_row: any, value: any) => (value !== null ? value : 0),
  },

  // ─── Gateway Performance Report V3 ──────────────────────────────────────S

  // Aggregate metrics (details_table / overview_cards)
  transactions: {
    label: 'Transactions',
    custom_label: 'Txn Counts',
    getter: (_row: any, value: any) => (value !== null && value !== undefined ? value : 0),
  },
  approvals: {
    label: 'Approvals',
    pct_label: 'Approvals (%)',
    getter: (row: any, value: any) => {
      let result: any = value !== null && value !== undefined ? value : 0;
      if (row['approvals_pct'] !== null && row['approvals_pct'] !== undefined) {
        result = String(result) + '&ensp;(' + formatPct(row['approvals_pct']) + ')';
      }
      return result;
    },
  },
  declines: {
    label: 'Declines',
    pct_label: 'Declines (%)',
    getter: (row: any, value: any) => {
      let result: any = value !== null && value !== undefined ? value : 0;
      if (row['declines_pct'] !== null && row['declines_pct'] !== undefined) {
        result = String(result) + '&ensp;(' + formatPct(row['declines_pct']) + ')';
      }
      return result;
    },
  },
  approved: {
    label: 'Approved Amount',
    custom_label: 'Approved $',
    getter: (_row: any, value: any) => convertToUSD(value),
  },
  approval_rate: {
    label: 'Approval Rate',
    getter: (_row: any, value: any) => (value !== null && value !== undefined ? value + '%' : '--'),
  },
  decline_rate: {
    label: 'Decline Rate',
    getter: (_row: any, value: any) => (value !== null && value !== undefined ? value + '%' : '--'),
  },
  approvals_pct: {
    label: 'Approvals %',
    getter: (_row: any, value: any) => (value !== null && value !== undefined ? value + '%' : '--'),
  },
  declines_pct: {
    label: 'Declines %',
    getter: (_row: any, value: any) => (value !== null && value !== undefined ? value + '%' : '--'),
  },
  gross_revenue: {
    label: 'Gross Revenue (Captured)',
    custom_label: 'Gross Revenue $',
    getter: (_row: any, value: any) => convertToUSD(value),
  },
  pending_settlement: {
    label: 'Pending Settlement',
    custom_label: 'Pending $',
    getter: (_row: any, value: any) => convertToUSD(value),
  },
  net_revenue: {
    label: 'Net Revenue (Settled)',
    custom_label: 'Net Revenue $',
    getter: (_row: any, value: any) => convertToUSD(value),
  },

  // Time dimensions
  date: {
    label: 'Date',
    getter: (_row: any, value: any) => (value ? value : '--'),
  },
  day_of_week: {
    label: 'Day of Week',
    getter: (_row: any, value: any) => {
      let result = '--';
      if (value !== null && value !== undefined) {
        const dayLabel = DayOfWeekLabels[Number(value)];
        result = dayLabel ? dayLabel : String(value);
      }
      return result;
    },
  },
  hour_of_day: {
    label: 'Hour of Day',
    getter: (_row: any, value: any) => (value !== null && value !== undefined ? String(value) : '--'),
  },
  month: {
    label: 'Month',
    getter: (_row: any, value: any) => (value ? value : '--'),
  },

  // Transaction & Lifecycle dimensions
  sales_type: {
    label: 'Transaction Lifecycle Type',
    custom_label: 'Txn Lifecycle Type',
    getter: (_row: any, value: any) => (value ? value : '--'),
  },
  attempt_number: {
    label: 'Attempt Number',
    getter: (_row: any, value: any) => (value !== null && value !== undefined ? value : '--'),
  },

  // Outcomes & Declines dimensions
  decline_category: {
    label: 'Decline Category',
    getter: (_row: any, value: any) => (value ? value : '--'),
  },
  decline_code: {
    label: 'Decline Reason Code',
    getter: (_row: any, value: any) => (value ? value : '--'),
  },
  decline_source: {
    label: 'Decline Source',
    getter: (_row: any, value: any) => (value ? value : '--'),
  },

  // Card & Issuer dimensions
  card_type: {
    label: 'Card Brand',
    getter: (_row: any, value: any) => (value ? value : '--'),
  },
  issuer_bank_name: {
    label: 'Bank',
    getter: (_row: any, value: any) => (value ? value : '--'),
  },
  issuer_country: {
    label: 'Issuer Country',
    getter: (_row: any, value: any) => (value ? value : '--'),
  },
  card_level: {
    label: 'Card Type',
    getter: (_row: any, value: any) => (value ? value : '--'),
  },
  card_bin: {
    label: 'BIN',
    getter: (_row: any, value: any) => (value ? value : '--'),
  },

  // Product & Traffic dimensions
  traffic_source: {
    label: 'Traffic Source',
    getter: (_row: any, value: any) => (value ? value : '--'),
  },
  gateway_name: {
    label: 'Merchant/Acquirer',
    getter: (_row: any, value: any) => (value ? value : '--'),
  },
  affiliate_id: {
    label: 'Affiliate ID',
    getter: (_row: any, value: any) => (value ? value : '--'),
  },
  sub_id: {
    label: 'Sub ID 1',
    getter: (_row: any, value: any) => (value ? value : '--'),
  },
  sub_id2: {
    label: 'Sub ID 2',
    getter: (_row: any, value: any) => (value ? value : '--'),
  },
  sub_id3: {
    label: 'Sub ID 3',
    getter: (_row: any, value: any) => (value ? value : '--'),
  },

  // Auth & Tokens dimensions
  three_ds_type: {
    label: '3DS Type',
    getter: (_row: any, value: any) => (value ? value : '--'),
  },
  three_ds_enabled: {
    label: '3DS Applied',
    getter: (_row: any, value: any) => (value !== null && value !== undefined ? (value ? 'Yes' : 'No') : '--'),
  },
  nt_used: {
    label: 'Token Used',
    getter: (_row: any, value: any) => (value !== null && value !== undefined ? (value ? 'Yes' : 'No') : '--'),
  },
  level3_enabled: {
    label: 'Level 3 Data',
    getter: (_row: any, value: any) => (value !== null && value !== undefined ? (value ? 'Yes' : 'No') : '--'),
  },
  refunds_pending: {
    label: 'Refunds Pending',
    getter: (_row: any, value: any) => (value ? value : '--'),
  },
  refund_count: {
    label: 'Refund Count',
    getter: (_row: any, value: any) => (value !== null && value !== undefined ? value : '--'),
  },
  refund_amount: {
    label: 'Refund Amount',
    custom_label: 'Refund $',
    getter: (_row: any, value: any) => convertToUSD(value),
  },
  refund_type: {
    label: 'Refund Type',
    getter: (_row: any, value: any) => (value ? value : '--'),
  },

  journey_id: {
    label: 'Journey ID',
    getter: (_row: any, value: any) => (value !== null && value !== undefined ? value : '--'),
  },
  initial_intent: {
    label: 'Initial Intent',
    getter: (_row: any, value: any) => {
      let result = '--';
      if (value !== null && value !== undefined && JourneyIntentLabels[value] !== undefined) {
        result = JourneyIntentLabels[value];
      }
      return result;
    },
  },
  final_intent: {
    label: 'Final Intent',
    getter: (_row: any, value: any) => {
      let result = '--';
      if (value !== null && value !== undefined && JourneyIntentLabels[value] !== undefined) {
        result = JourneyIntentLabels[value];
      }
      return result;
    },
  },
  customer_email: {
    label: 'Cx Email',
    getter: (_row: any, value: any) => (value ? value : '--'),
  },
  sub_attached: {
    label: 'Sub Attached',
    getter: (_row: any, value: any) => (value !== null && value !== undefined ? (value ? 'Yes' : 'No') : '--'),
  },
  sub_canceled: {
    label: 'Sub Canceled',
    getter: (_row: any, value: any) => (value !== null && value !== undefined ? (value ? 'Yes' : 'No') : '--'),
  },
  canceled_with_order: {
    label: 'Canceled With Order',
    getter: (_row: any, value: any) => (value !== null && value !== undefined ? (value ? 'Yes' : 'No') : '--'),
  },

  // Filter-only fields
  price_point: {
    label: 'Price Point',
    getter: (_row: any, value: any) => (value ? value : '--'),
  },
  transaction_type: {
    label: 'Transaction Type',
    getter: (_row: any, value: any) => (value ? value : '--'),
  },
  // Customer Support report - session_count is already defined above and reused as-is
  unique_sessions: {
    label: 'Unique Sessions',
    summaryHelpText: 'Return visits by the same customer within 8 hours are counted once. Sessions counts every visit.',
    getter: (_row: any, value: any) => countOrZero(value),
  },
  unique_customers: { label: 'Unique Customers', getter: (_row: any, value: any) => countOrZero(value) },
  automation_rate: { label: 'Automation Rate %', getter: (_row: any, value: any) => formatPct(value) },
  successful: { label: 'Successful', pct_label: 'Successful (%)', getter: countWithPct('successful') },
  bypass: { label: 'Bypass', pct_label: 'Bypass (%)', getter: countWithPct('bypass') },
  dropoffs: { label: 'Dropoffs', pct_label: 'Dropoffs (%)', getter: countWithPct('dropoffs') },
  lifeline: { label: 'Lifeline', pct_label: 'Lifeline (%)', getter: countWithPct('lifeline') },
  abandoned: { label: 'Abandoned', pct_label: 'Abandoned (%)', getter: countWithPct('abandoned') },
  inquiries: {
    label: 'Inquiries',
    pct_label: 'Inquiries (%)',
    summaryHelpText: 'Visits that raised a ticket, submitted a lifeline contact, or arrived by phone.',
    getter: countWithPct('inquiries'),
  },
  track_order_requests: {
    label: 'Track Order Requests',
    summaryHelpText: 'Visits where the customer selected Track Order. Same figure as the Track Order row in Top Intents.',
    getter: (_row: any, value: any) => countOrZero(value),
  },
  avg_visit_duration: { label: 'Avg Visit Duration', getter: (_row: any, value: any) => formatDurationSeconds(value) },

  // Enum dimensions arrive as integers; the labels are the API contract.
  // The Customer Journey Report sends channel as text ('Web'), which is shown as-is.
  channel: {
    label: 'Channel',
    getter: (_row: any, value: any) => (typeof value === 'string' ? value : enumLabel(SessionSourceLabels, value)),
  },
  moment: { label: 'Moment', getter: (_row: any, value: any) => enumLabel(MomentLabels, value) },
  session_type: { label: 'Session Type', getter: (_row: any, value: any) => enumLabel(SessionTypeLabels, value) },
  identification_status: {
    label: 'Identification Status',
    getter: (_row: any, value: any) => enumLabel(IdentificationStatusLabels, value),
  },
  intent: { label: 'Intent', getter: (_row: any, value: any) => (value ? value : '--') },
  result: {
    label: 'Result',
    getter: (_row: any, value: any): string => {
      let label = enumLabel(SessionResultLabels, value);
      if (value === SessionResult.Unknown) {
        label = 'Unknown';
      }
      return label;
    },
  },
  sid_1: { label: 'Sub ID', getter: (_row: any, value: any) => (value ? value : '--') },
  sid_2: { label: 'Sub ID 2', getter: (_row: any, value: any) => (value ? value : '--') },
  sid_3: { label: 'Sub ID 3', getter: (_row: any, value: any) => (value ? value : '--') },

  // Customer Journey Report fields (Journey ID drill-down). session_id, intent, channel and offer_name above are shared.
  session_started: {
    label: 'Date',
    getter: (_row: any, value: any, timezone: string) => {
      let result = '--';
      if (value) {
        result = moment.utc(value).tz(timezone).format('MMM DD, YYYY - hh:mm A');
      }
      return result;
    },
  },
  parent_session_id: { label: 'Parent Session', getter: (_row: any, value: any) => (value ? value : '--') },
  support_contact: { label: 'Support Contact', getter: (_row: any, value: any) => (value ? value : '--') },
  intent_type: {
    label: 'Intent Type',
    getter: (_row: any, value: any) => {
      let result = value ? value : '--';
      if (CustomerJourneyIntentTypeLabels[value]) {
        result = CustomerJourneyIntentTypeLabels[value];
      }
      return result;
    },
  },
  item: { label: 'Item', getter: (_row: any, value: any) => (value ? value : '--') },
  reason: { label: 'Reason', getter: (_row: any, value: any) => (value ? value : '--') },
  offer: { label: 'Offer', getter: (_row: any, value: any) => (value ? value : '--') },
  session_outcome: { label: 'Session Outcome', getter: (_row: any, value: any) => (value ? value : '--') },
  sessions: { label: 'Sessions', getter: (_row: ReportRow, value: number | null) => countOrZero(value) },
  lookups: {
    label: 'Lookups',
    summaryHelpText: 'Every digital-receipt lookup a bank app made in this period, matched or not.',
    getter: (_row: ReportRow, value: number | null) => countOrZero(value),
  },
  unmatched: {
    label: 'Unmatched',
    summaryHelpText: 'Lookups that found no order or no merchant, so no receipt was served.',
    getter: (_row: ReportRow, value: number | null) => countOrZero(value),
  },
  match_rate: {
    label: 'Match Rate',
    getter: (_row: ReportRow, value: number | null) => formatPct(value),
  },
  clicks: {
    label: 'Clicks',
    summaryHelpText: 'Sessions where the cardholder opened the receipt through the View Order link.',
    getter: (_row: ReportRow, value: number | null) => countOrZero(value),
  },
  ctr: {
    label: 'CTR',
    getter: (_row: ReportRow, value: number | null) => formatPct(value),
  },
  matched: { label: 'Matched', pct_label: 'Matched (%)', getter: countWithPct('matched') },
  link_clicks: { label: 'Link Clicks', pct_label: 'Link Clicks (%)', getter: countWithPct('link_clicks') },
  campaign: { label: 'Campaign', getter: pairLabel('campaign_name', 'campaign_id') },
  merchant_profile: {
    label: 'Merchant Profile',
    getter: pairLabel('merchant_profile_name', 'merchant_profile_id'),
  },
  product: { label: 'Product', getter: pairLabel('product_name', 'product_id') },
  crm: { label: 'CRM', getter: pairLabel('crm_name', 'crm_id') },
  // The Customer Journey Report sends outcome as text, which is shown as-is (with its own 'Outcome' header).
  outcome: {
    label: 'Lookup Outcome',
    getter: (_row: ReportRow, value: number | string | null) =>
      typeof value === 'string' && value ? value : enumLabel(ResolveLookupOutcomeLabels, value),
  },
  link_type: {
    label: 'Link Type',
    getter: (_row: ReportRow, value: string): string => {
      let label = '--';
      if (value) {
        label = ResolveLinkTypeLabels[value] ? ResolveLinkTypeLabels[value] : value;
      }
      return label;
    },
  },
  first_intent: {
    label: 'First Intent',
    getter: (_row: ReportRow, value: number | null) => enumLabel(JourneyIntentLabels, value),
  },
  originator_channel: {
    label: 'Originator Channel',
    getter: (_row: ReportRow, value: string) => (value ? value : '--'),
  },
};
